import { z } from 'zod'

// Task UI Component Types for the refactored component system

// Task field types that can be displayed
export const TaskFieldTypeSchema = z.enum([
  'status',
  'priority',
  'assignees',
  'scheduledFor',
  'dueDate',
  'storyPoints',
  'dependencies',
  'subtasks',
  'name',
  'taskNumber',
  'description',
  'group',
  'tags',
])

export type TaskFieldType = z.infer<typeof TaskFieldTypeSchema>

// Task display variants
export const TaskVariantSchema = z.enum(['list', 'card', 'table', 'compact'])

export type TaskVariant = z.infer<typeof TaskVariantSchema>

// Field configuration for customizing which fields to show
export const TaskFieldConfigSchema = z.object({
  field: TaskFieldTypeSchema,
  visible: z.boolean().default(true),
  editable: z.boolean().default(true),
  compact: z.boolean().default(false),
  order: z.number().optional(),
  width: z.string().optional(), // For table columns
  className: z.string().optional(),
})

export type TaskFieldConfig = z.infer<typeof TaskFieldConfigSchema>

// Complete task item configuration
export const TaskItemConfigSchema = z.object({
  variant: TaskVariantSchema,
  fields: z.array(TaskFieldConfigSchema).optional(),
  interactive: z.boolean().default(true),
  selectable: z.boolean().default(true),
  draggable: z.boolean().default(false),
  expandable: z.boolean().default(false),
  showSubtasks: z.boolean().default(false),
  compact: z.boolean().default(false),
  className: z.string().optional(),
})

export type TaskItemConfig = z.infer<typeof TaskItemConfigSchema>

// Status configuration for dropdowns and displays
export const TaskStatusOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string().optional(), // Display label if different from name
  color: z.string().optional(), // Hex color
  statusTypeId: z.string(), // NOT_STARTED, IN_PROGRESS, etc.
  icon: z.string().optional(), // Icon name or component identifier
  order: z.number().optional(),
})

export type TaskStatusOption = z.infer<typeof TaskStatusOptionSchema>

// Priority configuration
export const TaskPriorityOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
  color: z.string(),
  bars: z.number().min(0).max(3),
  isUrgent: z.boolean().default(false),
  order: z.number().optional(),
})

export type TaskPriorityOption = z.infer<typeof TaskPriorityOptionSchema>

// Update operation types for optimistic updates
export const TaskUpdateOptionsSchema = z.object({
  optimistic: z.boolean().default(true),
  silent: z.boolean().default(false),
  debounce: z.number().optional(), // ms to debounce updates
  onSuccess: z.function().optional(),
  onError: z.function().optional(),
})

export type TaskUpdateOptions = z.infer<typeof TaskUpdateOptionsSchema>

// Field update context for fine-grained updates
export const TaskFieldUpdateContextSchema = z.object({
  taskId: z.string(),
  field: TaskFieldTypeSchema,
  value: z.any(),
  previousValue: z.any().optional(),
  options: TaskUpdateOptionsSchema.optional(),
})

export type TaskFieldUpdateContext = z.infer<
  typeof TaskFieldUpdateContextSchema
>

// Drag and drop context
export const TaskDragDropContextSchema = z.object({
  draggingTaskId: z.string().nullable(),
  dropTargetId: z.string().nullable(),
  dragOverTaskId: z.string().nullable(),
  dropPosition: z.enum(['above', 'below', 'on']).nullable(),
  isValidDrop: z.boolean().default(false),
})

export type TaskDragDropContext = z.infer<typeof TaskDragDropContextSchema>

// Selection context for bulk operations
export const TaskSelectionContextSchema = z.object({
  selectedTaskIds: z.array(z.string()),
  selectionMode: z.boolean().default(false),
  selectAll: z.boolean().default(false),
  lastSelectedId: z.string().nullable(),
})

export type TaskSelectionContext = z.infer<typeof TaskSelectionContextSchema>

// Filter and view state
export const TaskViewStateSchema = z.object({
  variant: TaskVariantSchema,
  groupBy: z.string().nullable(),
  sortBy: z.string().nullable(),
  sortDirection: z.enum(['asc', 'desc']).default('asc'),
  filters: z.record(z.any()).default({}),
  searchQuery: z.string().default(''),
  expandedGroups: z.array(z.string()).default([]),
  expandedTasks: z.array(z.string()).default([]),
})

export type TaskViewState = z.infer<typeof TaskViewStateSchema>

// Component event handlers
export interface TaskComponentEvents {
  onTaskClick?: (taskId: string) => void
  onTaskUpdate?: (taskId: string, updates: Record<string, any>) => void
  onTaskDelete?: (taskId: string) => void
  onTaskSelect?: (taskId: string, selected: boolean) => void
  onTaskExpand?: (taskId: string, expanded: boolean) => void
  onFieldUpdate?: (context: TaskFieldUpdateContext) => void
  onDragStart?: (taskId: string) => void
  onDragEnd?: (taskId: string) => void
  onDrop?: (draggedId: string, targetId: string, position: string) => void
}

// Memoization and performance
export const TaskMemoKeySchema = z.object({
  taskId: z.string(),
  version: z.number().optional(), // For cache busting
  variant: TaskVariantSchema,
  fieldHash: z.string().optional(), // Hash of visible fields
  interactionHash: z.string().optional(), // Hash of event handlers
})

export type TaskMemoKey = z.infer<typeof TaskMemoKeySchema>

// Error handling
export const TaskComponentErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  field: TaskFieldTypeSchema.optional(),
  taskId: z.string().optional(),
  retry: z.function().optional(),
})

export type TaskComponentError = z.infer<typeof TaskComponentErrorSchema>

// Loading states
export const TaskLoadingStateSchema = z.object({
  isLoading: z.boolean().default(false),
  loadingField: TaskFieldTypeSchema.nullable(),
  loadingOperation: z.string().nullable(), // 'update', 'delete', 'create'
  progress: z.number().min(0).max(100).optional(),
})

export type TaskLoadingState = z.infer<typeof TaskLoadingStateSchema>

// Constants for default configurations
export const DEFAULT_FIELD_CONFIGS = {
  list: ['status', 'priority', 'assignees', 'dueDate'] as TaskFieldType[],
  card: ['assignees', 'dueDate', 'priority'] as TaskFieldType[],
  table: [
    'status',
    'priority',
    'assignees',
    'dueDate',
    'storyPoints',
    'group',
  ] as TaskFieldType[],
  compact: ['status', 'priority'] as TaskFieldType[],
} as const

export const DEFAULT_STATUS_TYPES = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  ON_HOLD: 'ON_HOLD',
  CANCELED: 'CANCELED',
} as const

export type StatusType = keyof typeof DEFAULT_STATUS_TYPES
